import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { getCompletedQuizzes, getQuizHistoryFilterOptions } from '../services/quizService';
import {
  QuizHistoryItem,
  QuizHistoryFilters,
  PaginationParams,
  DifficultyLevel,
  QuizHistoryFilterOptions,
  FilterOption
} from '../types';
import {
  Card,
  CardBody,
  Section,
  Button,
  Badge
} from '../components/BaseComponents';

/**
 * QuizHistoryPage Component
 * 
 * Displays the user's complete quiz history with filtering and pagination.
 * Features:
 * - Chronological list of completed quizzes
 * - Filtering by date range, difficulty, and content source
 * - Pagination for large result sets
 * - Detailed quiz information with scores and timing
 * - Quick access to review individual quiz results
 */
export const QuizHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();

  // State for quiz history data
  const [quizHistory, setQuizHistory] = useState<QuizHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for filtering
  const [filters, setFilters] = useState<QuizHistoryFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState<QuizHistoryFilterOptions | null>(null);
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const itemsPerPage = 10;

  // Load quiz history
  useEffect(() => {
    loadQuizHistory();
  }, [currentUser, filters, currentPage]);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!filterOptions && !loadingFilterOptions) {
        setLoadingFilterOptions(true);
        try {
          const options = await getQuizHistoryFilterOptions();
          setFilterOptions(options);
        } catch (err) {
          console.error('Error loading filter options:', err);
        } finally {
          setLoadingFilterOptions(false);
        }
      }
    };

    loadFilterOptions();
  }, [filterOptions, loadingFilterOptions]);

  const loadQuizHistory = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const pagination: PaginationParams = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'completedAt',
        sortOrder: 'DESC'
      };

      const result = await getCompletedQuizzes(currentUser.userId, filters, pagination);

      setQuizHistory(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalQuizzes(result.pagination.total);
    } catch (err) {
      console.error('Error loading quiz history:', err);
      setError('Failed to load quiz history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<QuizHistoryFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  // Format duration for display
  const formatDuration = (minutes?: number): string => {
    if (!minutes) return 'Unknown';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Format date for display
  const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get difficulty badge color
  const getDifficultyColor = (difficulty: DifficultyLevel): string => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get score badge color based on percentage
  const getScoreColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-green-100 text-green-800';
    if (percentage >= 70) return 'bg-blue-100 text-blue-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Please log in to view your quiz history.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <Section className="section-spacing">
          <Card className="card-hero" noHover={true}>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-page-title">Quiz History</h1>
                  <p className="text-indigo-100">
                    Review your completed quizzes and track your progress over time
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{totalQuizzes}</div>
                  <div className="text-indigo-200">Completed Quizzes</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Section>

        {/* Filters */}
        <Section className="mb-6">
          <Card>
            <CardBody>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-content-title text-gray-900">Filter Results</h3>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </Button>
                  {Object.keys(filters).length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              {showFilters && (
                <div className="border-t pt-4">
                  <div className="grid-responsive-3 gap-4">
                    <div>
                      <label htmlFor="difficulty" className="form-label">
                        Difficulty
                      </label>
                      <select
                        id="difficulty"
                        value={filters.difficulty || ''}
                        onChange={(e) => handleFilterChange({
                          difficulty: e.target.value ? e.target.value as DifficultyLevel : undefined
                        })}
                        className="form-select"
                      >
                        <option value="">All Difficulties</option>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="startDate" className="form-label">
                        From Date
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        value={filters.startDate ? filters.startDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => handleFilterChange({
                          startDate: e.target.value ? new Date(e.target.value) : undefined
                        })}
                        className="form-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="endDate" className="form-label">
                        To Date
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        value={filters.endDate ? filters.endDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => handleFilterChange({
                          endDate: e.target.value ? new Date(e.target.value) : undefined
                        })}
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Hierarchy Filters */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Filter by Content Hierarchy</h4>
                    <div className="grid-responsive-3 gap-4">
                      <div>
                        <label htmlFor="yearNumber" className="form-label">
                          Year
                        </label>
                        <select
                          id="yearNumber"
                          value={filters.yearNumber || ''}
                          onChange={(e) => handleFilterChange({
                            yearNumber: e.target.value || undefined
                          })}
                          className="form-select"
                          disabled={loadingFilterOptions}
                        >
                          <option value="">All Years</option>
                          {filterOptions?.years.map(year => (
                            <option key={year.value} value={year.value}>{year.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="stageId" className="form-label">
                          Stage
                        </label>
                        <select
                          id="stageId"
                          value={filters.stageId || ''}
                          onChange={(e) => handleFilterChange({
                            stageId: e.target.value || undefined
                          })}
                          className="form-select"
                          disabled={loadingFilterOptions}
                        >
                          <option value="">All Stages</option>
                          {filterOptions?.stages.map(stage => (
                            <option key={stage.value} value={stage.value}>{stage.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="strandId" className="form-label">
                          Strand
                        </label>
                        <select
                          id="strandId"
                          value={filters.strandId || ''}
                          onChange={(e) => handleFilterChange({
                            strandId: e.target.value || undefined
                          })}
                          className="form-select"
                          disabled={loadingFilterOptions}
                        >
                          <option value="">All Strands</option>
                          {filterOptions?.strands.map(strand => (
                            <option key={strand.value} value={strand.value}>{strand.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid-responsive-2 gap-4 mt-3">
                      <div>
                        <label htmlFor="substrandId" className="form-label">
                          Substrand
                        </label>
                        <select
                          id="substrandId"
                          value={filters.substrandId || ''}
                          onChange={(e) => handleFilterChange({
                            substrandId: e.target.value || undefined
                          })}
                          className="form-select"
                          disabled={loadingFilterOptions}
                        >
                          <option value="">All Substrands</option>
                          {filterOptions?.substrands.map(substrand => (
                            <option key={substrand.value} value={substrand.value}>{substrand.label}</option>
                          ))}
                        </select>
                      </div>

                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Section>

        {/* Results */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600">Loading quiz history...</span>
          </div>
        )}

        {error && (
          <Section>
            <Card className="border-red-200 bg-red-50">
              <CardBody>
                <div className="flex items-center justify-between">
                  <p className="text-red-700">{error}</p>
                  <Button onClick={loadQuizHistory} variant="outline" size="sm">
                    Try Again
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Section>
        )}

        {!loading && !error && quizHistory.length === 0 && (
          <Section>
            <Card>
              <CardBody className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📚</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Quiz History Found</h3>
                <p className="text-gray-600 mb-6">
                  You haven't completed any quizzes yet, or no quizzes match your current filters.
                </p>
                <div className="space-x-3">
                  <Button
                    variant="primary"
                    onClick={() => navigate('/categories')}
                  >
                    Take Your First Quiz
                  </Button>
                  {Object.keys(filters).length > 0 && (
                    <Button variant="secondary" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          </Section>
        )}

        {!loading && !error && quizHistory.length > 0 && (
          <>
            {/* Quiz List */}
            <Section>
              <div className="space-y-4">
                {quizHistory.map((quiz) => (
                  <Card key={quiz.attemptId} className="hover:shadow-lg transition-all duration-200">
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {/* Show the quiz title */}
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {quiz.source?.displayName || 'General Quiz'}
                          </h3>

                          {/* Show educational hierarchy context */}
                          {quiz.source?.hierarchyContext && (
                            <div className="text-sm text-gray-500 mb-2">
                              {quiz.source.hierarchyContext.yearNumber && `Year ${quiz.source.hierarchyContext.yearNumber}`}
                              {quiz.source.hierarchyContext.yearNumber && quiz.source.hierarchyContext.stageCode && ' • '}
                              {quiz.source.hierarchyContext.stageCode && quiz.source.hierarchyContext.stageCode}
                              {quiz.source.hierarchyContext.stageCode && quiz.source.hierarchyContext.strandName && ' • '}
                              {quiz.source.hierarchyContext.strandName && quiz.source.hierarchyContext.strandName}
                              {quiz.source.hierarchyContext.strandName && quiz.source.hierarchyContext.substrandName && ' • '}
                              {quiz.source.hierarchyContext.substrandName && quiz.source.hierarchyContext.substrandName}
                            </div>
                          )}
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge className={getDifficultyColor(quiz.difficulty)}>
                              {quiz.difficulty.charAt(0).toUpperCase() + quiz.difficulty.slice(1)}
                            </Badge>
                            <Badge className={getScoreColor(quiz.scorePercentage)}>
                              {quiz.scorePercentage}%
                            </Badge>
                            <Badge className="bg-gray-100 text-gray-800">
                              {quiz.correctAnswers}/{quiz.totalQuestions} correct
                            </Badge>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">
                                Completed {formatDate(quiz.completedAt!)}
                              </p>
                              {quiz.duration ? <p className="text-sm text-gray-500">
                                Duration: {formatDuration(quiz.duration)}
                              </p> : <></>}
                            </div>

                            <div className="flex items-center space-x-3">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/history/${quiz.attemptId}`)}
                              >
                                View Results
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </Section>

            {/* Pagination */}
            {totalPages > 1 && (
              <Section>
                <Card>
                  <CardBody>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, totalQuizzes)} of {totalQuizzes} quizzes
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </Button>

                        <span className="text-sm text-gray-600">
                          Page {currentPage} of {totalPages}
                        </span>

                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuizHistoryPage;